"use client";

import React from "react";
import { ArrowRight, ShieldCheck, Clock, CircleCheck as CheckCircle2 } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const servicesData = [{
  id: "water-restoration",
  title: "Water Damage & Flood Restoration",
  tag: "Rapid Extraction",
  to: "/WaterDamageFloodRestorationPage",
  image: {
    src: "https://triserveinc.com/wp-content/uploads/2025/09/Flooded-Basement-768x576-1.jpg?v=5g14j06aeh8",
    srcset: "https://triserveinc.com/wp-content/uploads/2025/09/Flooded-Basement-768x576-1.jpg?v=qg9rx7lr1m-wvc-srcset",
    sizes: "https://triserveinc.com/wp-content/uploads/2025/09/Flooded-Basement-768x576-1.jpg?v=4veudo6uz83-wvc-sizes",
    alt: "Restoration crew deploying industrial water extraction vacuums in flooded building"
  },
  description: "Our certified crews extract standing water quickly using commercial truck-mounted pumps and structural dehumidifiers. We dry saturated framing and verify moisture levels with infrared thermal cameras."
}, {
  id: "mold-remediation",
  title: "Mold Remediation",
  tag: "HEPA Containment",
  to: "/MoldRemediationPage",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/mold_remediation_containment.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/mold_remediation_containment.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/mold_remediation_containment.png-wvc-sizes",
    alt: "Technician setting up negative air scrubbers and containment barriers for mold removal"
  },
  description: "We isolate active colonies under negative pressure to keep spores from spreading through HVAC channels. Technicians remove colonized materials and sanitize surrounding surfaces with antimicrobial agents."
}, {
  id: "fire-smoke",
  title: "Fire, Smoke & Odor Restoration",
  tag: "Thermal Deodorization",
  to: "/FireSmokeOdorRestorationPage",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/fire_smoke_restoration_cleaning.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/fire_smoke_restoration_cleaning.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/fire_smoke_restoration_cleaning.png-wvc-sizes",
    alt: "Field crew treating soot damage and thermal smoke odor on structural ceiling beams"
  },
  description: "We remove caustic soot residues before they etch permanently into walls, tile, and framing. Thermal fogging and hydroxyl generators eliminate stubborn smoke odor down to the studs."
}, {
  id: "hazardous-materials",
  title: "Hazardous Materials Cleanup",
  tag: "Certified Protocol",
  to: "/SewageCleanupPage",
  image: {
    src: "https://triserveinc.com/wp-content/uploads/TriServe-Emergency-Responce-Team.jpg?v=do4uv6gj7vd",
    srcset: "https://triserveinc.com/wp-content/uploads/TriServe-Emergency-Responce-Team.jpg?v=k2surcu0o3a-wvc-srcset",
    sizes: "https://triserveinc.com/wp-content/uploads/TriServe-Emergency-Responce-Team.jpg?v=ookyx6y46gk-wvc-sizes",
    alt: "Technicians in protective suits handling certified sewage and biohazard cleanup"
  },
  description: "We treat black-water sewage overflows, trauma sites, and hazardous contaminants with clinical containment. All affected materials are extracted and disposed of following strict local environmental laws."
}, {
  id: "reconstruction",
  title: "Reconstruction & Design-Build",
  tag: "Turn-Key Rebuild",
  to: "/ReconstructionDesignBuildPage",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/reconstruction_structural_framing.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/reconstruction_structural_framing.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/reconstruction_structural_framing.png-wvc-sizes",
    alt: "Carpenters installing new drywall and structural studs during property reconstruction"
  },
  description: "Once mitigation is complete, our licensed builders manage drywall, trim, flooring, and electrical restoration. You work with one accountable contractor from the initial flood call through final walkthrough."
}, {
  id: "commercial-hoa",
  title: "Commercial & HOA Property Restoration",
  tag: "Multi-Unit Response",
  to: "/CommercialHOAPropertyRestorationPage",
  image: {
    src: "https://triserveinc.com/wp-content/uploads/triserve-flood-cleanup-emergency-fast-response.webp?v=1hjwwksujq7",
    srcset: "https://triserveinc.com/wp-content/uploads/triserve-flood-cleanup-emergency-fast-response.webp?v=itk9bn4m6m-wvc-srcset",
    sizes: "https://triserveinc.com/wp-content/uploads/triserve-flood-cleanup-emergency-fast-response.webp?v=ct4302ou3xi-wvc-sizes",
    alt: "TriServe fleet parked outside commercial property complex for emergency mitigation"
  },
  description: "We mobilize large-scale equipment to restore multi-family properties and office facilities without business disruption. Our managers work directly with property boards and insurance claims adjusters."
}];
export default function Services() {
  const shouldReduceMotion = useReducedMotion();
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0.01 : 0.35,
        delay: shouldReduceMotion ? 0 : i * 0.08,
        ease: [0.16, 1, 0.3, 1]
      }
    })
  };
  return <section id="services-overview" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Technical Operational Spec Bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 border border-border bg-muted/60 px-4 py-2.5 rounded-md mb-10 text-xs sm:text-sm">
          <div className="flex items-center gap-2 font-mono text-foreground font-semibold">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary"></span>
            </span>
            <span>24/7 RAPID DISPATCH</span>
            <span className="text-muted-foreground hidden sm:inline">•</span>
            <span className="text-muted-foreground hidden sm:inline">MARYLAND, DC & NORTHERN VIRGINIA</span>
          </div>

          <div className="flex items-center gap-4 text-muted-foreground font-mono text-xs">
            <span className="flex items-center gap-1.5">
              <Clock className="size-3.5 text-primary" />
              Direct Technician Response
            </span>
            <span className="hidden md:flex items-center gap-1.5">
              <ShieldCheck className="size-3.5 text-secondary" />
              Insurance Coordination
            </span>
          </div>
        </div>

        {/* Section Heading Block */}
        <div className="max-w-3xl mb-12 sm:mb-16">
          <p className="text-xs sm:text-sm font-bold tracking-wider text-primary uppercase mb-2">
            Disaster Mitigation & Recovery
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-foreground font-sans">
            Full-service disaster recovery
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Local crews equipped for rapid containment, certified removal, and complete reconstruction. We coordinate directly with your insurance adjuster from start to finish.
          </p>
        </div>

        {/* 6-Card Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {servicesData.map((service, index) => <motion.div key={service.id} data-index={index} custom={index} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.15
        }} variants={cardVariants} className="flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card rounded-lg overflow-hidden shadow-xs hover:border-primary/50 hover:shadow-md transition-[border-color,box-shadow] duration-200">
                <div>
                  {/* 16:10 Ratio Photo Header */}
                  <div className="relative aspect-[16/10] w-full overflow-hidden bg-muted border-b border-border">
                    <img src={service.image.src} data-wvc-srcset={service.image.srcset} data-wvc-sizes={service.image.sizes} alt={service.image.alt} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                    <div className="absolute top-3 right-3 bg-background/90 backdrop-blur-xs text-foreground px-2.5 py-1 rounded-sm text-[11px] font-mono font-medium border border-border shadow-2xs">
                      {service.tag}
                    </div>
                  </div>

                  <CardHeader className="px-6 pt-5 pb-2">
                    <CardTitle className="text-xl sm:text-2xl font-bold font-sans text-foreground leading-snug">
                      {service.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="px-6 pt-1 pb-4">
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {service.description}
                    </p>
                  </CardContent>
                </div>

                <CardFooter className="px-6 pb-6 pt-2 border-t border-border/40 mt-auto">
                  <Button asChild variant="outline" className="w-full justify-between font-semibold text-primary border-primary/40 hover:bg-primary/5 hover:text-primary hover:border-primary transition-colors">
                    <Link to={service.to}>
                      <span>Learn More</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </div>

        {/* Reassurance Footer Banner */}
        <div className="mt-12 p-6 rounded-lg bg-muted/40 border border-border flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="size-5 text-primary shrink-0" />
            <p className="text-sm font-medium text-foreground">
              No outsourced call centers — every dispatch connects you straight to licensed regional technicians.
            </p>
          </div>
          <div className="shrink-0 flex items-center gap-3">
            <Button asChild className="bg-primary text-primary-foreground font-bold hover:bg-primary/90">
              <Link to="/ContactRequestServicePage">
                Request Service Now
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}